import { CfgName } from './cfg.enum';

/**
 * M2-D69 — os sons de aviso que o painel sabe tocar. Só os IDS moram aqui (as duas pontas precisam deles: o
 * servidor para o padrão da cfg, a tela para escolher e tocar); a síntese de cada um mora no frontend
 * (`libs/frontend/ui/src/lib/alert-sound`), que é quem toca.
 *
 * `classic` é o bipe curto de antes da M2-F3, mantido para quem se acostumou.
 */
export const ALERT_SOUND_IDS = ['chime', 'bell', 'ding-dong', 'marimba', 'insistent', 'classic'] as const;

export type AlertSoundId = (typeof ALERT_SOUND_IDS)[number];

/** Um tipo de aviso do painel e a cfg da empresa que escolhe o som dele (M2-D68). */
export interface AlertSoundSlotDefinition {
  readonly slot: string;
  readonly cfgName: CfgName;
  readonly defaultSound: AlertSoundId;
}

/**
 * M2-D68 — os tipos de aviso com som configurável, e a lista que a tela de Configurações percorre.
 *
 * **Tipo novo é uma entrada aqui**, uma `CfgName` e as chaves de i18n `alert-sound.slot.<slot minúsculo>` — nada
 * de tela nova. O padrão de cada tipo também sai daqui para o `defaultCfgs` do servidor: uma fonte só.
 */
export const ALERT_SOUND_SLOTS = [
  { slot: 'NEW_ORDER', cfgName: CfgName.NEW_ORDER_SOUND, defaultSound: 'chime' },
  { slot: 'WAITER_CALL', cfgName: CfgName.WAITER_CALL_SOUND, defaultSound: 'ding-dong' },
] as const satisfies readonly AlertSoundSlotDefinition[];

export type AlertSoundSlot = (typeof ALERT_SOUND_SLOTS)[number]['slot'];

export function isAlertSoundId(value: unknown): value is AlertSoundId {
  return typeof value === 'string' && (ALERT_SOUND_IDS as readonly string[]).includes(value);
}

/** O som que vale para o tipo: o gravado, se for um id conhecido; senão o padrão do tipo. */
export function resolveAlertSound(slot: AlertSoundSlot, value: string | null | undefined): AlertSoundId {
  if (isAlertSoundId(value)) {
    return value;
  }
  return ALERT_SOUND_SLOTS.find((definition) => definition.slot === slot)?.defaultSound ?? 'chime';
}
